import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import { INSIGHTS_BACKEND_URL, NEST_BACKEND_URL } from "@/lib/axios";
import { QueryConfig } from "@/lib/react-query";
import { useModuleEndpoint } from "@/shared/hooks/useModuleEndpoint";
import { axiosApi } from "@/Utils";

const getAllPublishersV2 = ({
  startDate,
  endDate,
  brand,
  category,
  product,
  geography,
}: {
  startDate?: string | null;
  endDate?: string | null;
  brand?: string;
  category?: string;
  product?: string;
  geography?: string;
}): Promise<string[]> => {
  const payload: Record<string, any> = {
    startDate,
    endDate,
    brand,
    category,
    product,
    geography,
  };

  return axiosApi.get("/api/v2/publishers/all", { params: payload, baseURL: INSIGHTS_BACKEND_URL });
};

const _getAllPublishersNest = ({
  startDate,
  endDate,
  brand,
  category,
  product,
  geography,
}: {
  startDate?: string | null;
  endDate?: string | null;
  brand?: string;
  category?: string;
  product?: string;
  geography?: string;
}): Promise<string[]> => {
  const params = new URLSearchParams();

  if (startDate) params.append("startDate", startDate);
  if (endDate) params.append("endDate", endDate);
  if (brand) params.append("brand", brand);
  if (category) params.append("category", category);
  if (product) params.append("product", product);
  if (geography) params.append("geography", geography);

  return axiosApi
    .get(`/api/publishers/all?${params.toString()}`, {
      baseURL: NEST_BACKEND_URL,
    })
    .then((response: any) => {
      return response.publishers;
    });
};

export const useAllPublishers = ({
  startDate,
  endDate,
  brand,
  category,
  product,
  geography,
  config,
}: {
  startDate?: string | null;
  endDate?: string | null;
  brand?: string;
  category?: string;
  product?: string;
  geography?: string;
  config?: QueryConfig<typeof getAllPublishersV2 | typeof _getAllPublishersNest>;
} = {}) => {
  const endpointType = useModuleEndpoint("publishers");

  const queryKey = useMemo(
    () => ["publishers", startDate, endDate, brand, category, product, geography, endpointType],
    [startDate, endDate, brand, category, product, geography, endpointType]
  );

  const queryFn = useCallback(() => {
    if (endpointType === "new") {
      return _getAllPublishersNest({
        startDate,
        endDate,
        brand,
        category,
        product,
        geography,
      });
    } else {
      return getAllPublishersV2({
        startDate,
        endDate,
        brand,
        category,
        product,
        geography,
      });
    }
  }, [startDate, endDate, brand, category, product, geography, endpointType]);

  return useQuery({
    ...config,
    queryKey,
    queryFn,
  });
};
